import { APIError, isApiErrorResponse } from "./api-error";
import { ApiErrorDTO } from "./apis";

let baseURL = "<BACKEND URL>";

export const setBaseUrl = (url: string): void => {
  baseURL = url;
};

export const getBaseUrl = (): string => {
  return baseURL;
};

let responseIsNotOkCallback = <T>(response: Response, data: Awaited<T>) => {
  if (!response.ok) {
    if (isApiErrorResponse(data as ApiErrorDTO)) {
      throw new APIError("The request has failed.", data as ApiErrorDTO);
    }
    throw new APIError("The request has failed.", {
      status: 500,
      code: "",
      message: "",
      timestamp: new Date().toISOString(),
    });
  }
};

export function setResponseIsNotOkCallback(
  func: <T>(response: Response, data: Awaited<T>) => void
) {
  responseIsNotOkCallback = func;
}

const checkStatus = (response: Response): void => {
  if (
    ![200, 201, 400, 401, 403, 404, 409, 500].find(
      (element) => element === response.status
    )
  ) {
    throw new APIError("The response has an unknown http status code.", {
      status: 500,
      code: "UNKNOWN_HTTP_STATUS_CODE",
      message: `The response has an unknown http status code: ${response.status}`,
      timestamp: new Date().toISOString(),
    });
  }
};

const getBody = <T>(c: Response | Request): Promise<T> => {
  const contentType = c.headers.get("content-type");

  if (contentType === null) {
    return c.text() as Promise<T>;
  }

  if (contentType && contentType.includes("application/json")) {
    return c.json();
  }

  if (
    contentType &&
    [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "application/zip",
    ].find((element) => element === contentType)
  ) {
    return c.blob() as Promise<T>;
  }

  if (contentType && contentType.includes("text/csv")) {
    return c.blob() as Promise<T>;
  }

  throw new APIError("The response has an unknown content-type.", {
    status: 500,
    code: "UNKNOWN_CONTENT_TYPE",
    message: `The response has an unknown content-type: ${contentType}`,
    timestamp: new Date().toISOString(),
  });
};

const getUrl = (contextUrl: string): string => {
  const url = new URL(contextUrl);
  const pathname = url.pathname;
  const search = url.search;
  const requestUrl = new URL(`${baseURL}${pathname}${search}`);

  return requestUrl.toString();
};

const getHeaders = (headers?: HeadersInit): HeadersInit => {
  const name = "XSRF-TOKEN=";
  const decodedCookie = decodeURIComponent(document.cookie);

  const ca = decodedCookie.split(";").map((ca) => ca.trim());
  const match = ca.find((ca) => ca.indexOf(name) !== -1);
  return {
    ...headers,
    ...(match && {
      "X-XSRF-TOKEN": match.substring(name.length, match.length),
    }),
  };
};

const getValidAbortSignal = (
  signal: RequestInit["signal"]
): RequestInit["signal"] | undefined => {
  if (!signal) {
    return undefined;
  }

  try {
    // Ensure the signal is accepted by the current fetch implementation.
    new Request("http://localhost", { signal });
    return signal;
  } catch {
    return undefined;
  }
};

export const customFetchInstance = async <T>(
  url: string,
  options: RequestInit
): Promise<T> => {
  const requestUrl = getUrl(url);
  const requestHeaders = getHeaders(options.headers);
  const { signal, ...restOptions } = options;

  const requestInit: RequestInit = {
    ...restOptions,
    headers: requestHeaders,
    signal: getValidAbortSignal(signal),
  };

  const response = await fetch(requestUrl, requestInit);
  checkStatus(response);
  const data = await getBody<T>(response);

  responseIsNotOkCallback(response, data);

  return {
    data,
    status: response.status,
    ok: response.ok,
  } as T;
};
